import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { X, Check, Send, MessageSquarePlus } from 'lucide-react';
import { useSurvey } from '../../contexts/SurveyContext';
import { useAnnouncement } from '../../contexts/AnnouncementContext';
import type { SurveyQuestion, SurveyAnswers } from '../../types/survey';

const SurveyPopup: React.FC = () => {
  const { survey, isOpen, closeSurvey, submitResponse } = useSurvey();
  const { isOpen: isAnnouncementOpen, loading: isAnnouncementLoading } = useAnnouncement();
  const [answers, setAnswers] = useState<SurveyAnswers>({});
  const [submitting, setSubmitting] = useState(false);

  // Reset local answers whenever a different survey is shown.
  const surveyId = survey?.id;
  useEffect(() => { setAnswers({}); }, [surveyId]);

  // Announcements have priority. The survey remains queued and appears after
  // the announcement is dismissed, so two modal backdrops never overlap.
  if (!isOpen || !survey || isAnnouncementLoading || isAnnouncementOpen) return null;
  const questions = survey.questions || [];

  const toggleMulti = (qId: number, key: string) => {
    setAnswers((prev) => {
      const cur = Array.isArray(prev[qId]) ? (prev[qId] as string[]) : [];
      return { ...prev, [qId]: cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key] };
    });
  };
  const setSingle = (qId: number, val: string | number) =>
    setAnswers((prev) => ({ ...prev, [qId]: val }));

  const isAnswered = (q: SurveyQuestion): boolean => {
    const a = answers[q.id as number];
    if (Array.isArray(a)) return a.length > 0;
    if (typeof a === 'number') return true;
    return typeof a === 'string' && a.trim() !== '';
  };

  const handleSubmit = async () => {
    const missing = questions.find((q) => q.is_required && !isAnswered(q));
    if (missing) {
      toast.error('Please answer the required question before submitting.');
      return;
    }
    try {
      setSubmitting(true);
      await submitResponse(answers);
      toast.success('Thanks for your feedback!');
    } catch (err: any) {
      toast.error(err?.message || 'Could not submit your response. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[88vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-gray-100">
          <div className="pr-4">
            <div className="inline-flex items-center gap-1.5 text-[0.6rem] font-semibold uppercase tracking-wide text-gray-400 mb-1">
              <MessageSquarePlus size={12} /> Your feedback
            </div>
            <h2 className="text-base font-semibold text-gray-900 leading-snug">{survey.title}</h2>
            {survey.description && (
              <p className="text-[0.75rem] text-gray-500 mt-1.5 leading-relaxed">{survey.description}</p>
            )}
          </div>
          <button
            onClick={() => closeSurvey(true)}
            aria-label="Dismiss"
            className="shrink-0 text-gray-400 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 overflow-y-auto space-y-6">
          {questions.map((q) => (
            <Question
              key={q.id}
              q={q}
              value={answers[q.id as number]}
              onToggleMulti={(key) => toggleMulti(q.id as number, key)}
              onSetSingle={(val) => setSingle(q.id as number, val)}
            />
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-4 border-t border-gray-100">
          <button
            onClick={() => closeSurvey(true)}
            className="text-[0.78rem] text-gray-500 hover:text-gray-800 px-2 py-2"
          >
            Maybe later
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex items-center gap-1.5 bg-black text-white text-[0.78rem] font-medium px-5 py-2.5 rounded-full hover:bg-gray-800 disabled:opacity-50"
          >
            <Send size={14} /> {submitting ? 'Submitting…' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
};

const Question: React.FC<{
  q: SurveyQuestion;
  value: string[] | number | string | undefined;
  onToggleMulti: (key: string) => void;
  onSetSingle: (val: string | number) => void;
}> = ({ q, value, onToggleMulti, onSetSingle }) => {
  const selected = Array.isArray(value) ? value : [];

  return (
    <div>
      <p className="text-[0.82rem] font-medium text-gray-900 mb-3">
        {q.question_text}
        {q.is_required && <span className="text-red-500 ml-1">*</span>}
        {q.question_type === 'multi' && (
          <span className="text-[0.65rem] font-normal text-gray-400 ml-2">(select all that apply)</span>
        )}
      </p>

      {(q.question_type === 'multi' || q.question_type === 'single') && (
        <div className="space-y-2">
          {q.options.map((opt) => {
            const isSel = q.question_type === 'multi' ? selected.includes(opt.key) : value === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => (q.question_type === 'multi' ? onToggleMulti(opt.key) : onSetSingle(opt.key))}
                className={`w-full text-left flex items-start gap-3 p-3 rounded-xl border transition-colors ${
                  isSel ? 'border-black bg-gray-50' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <span
                  className={`shrink-0 mt-0.5 w-4 h-4 flex items-center justify-center border ${
                    q.question_type === 'multi' ? 'rounded' : 'rounded-full'
                  } ${isSel ? 'bg-black border-black text-white' : 'border-gray-300'}`}
                >
                  {isSel && <Check size={11} strokeWidth={3} />}
                </span>
                <span>
                  <span className="block text-[0.78rem] font-medium text-gray-900">{opt.label}</span>
                  {opt.description && (
                    <span className="block text-[0.7rem] text-gray-500 leading-relaxed mt-0.5">{opt.description}</span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {(q.question_type === 'rating' || q.question_type === 'nps') && (
        <div className="flex flex-wrap gap-2">
          {Array.from(
            { length: q.question_type === 'rating' ? 5 : 11 },
            (_, i) => (q.question_type === 'rating' ? i + 1 : i),
          ).map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => onSetSingle(n)}
              className={`w-9 h-9 rounded-lg border text-[0.75rem] font-medium ${
                value === n ? 'bg-black text-white border-black' : 'border-gray-200 text-gray-700 hover:border-gray-400'
              }`}
            >
              {n}
            </button>
          ))}
        </div>
      )}

      {q.question_type === 'text' && (
        <textarea
          rows={3}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onSetSingle(e.target.value)}
          placeholder="Type your answer…"
          className="w-full text-[0.8rem] border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-black"
        />
      )}
    </div>
  );
};

export default SurveyPopup;
